<template>
    <!-- The one QR scanner in the app. Same camera as the checklist photos
         (utils/camera.js): the stream is opened when the dialog opens and
         stopped when it closes AND when the component unmounts, so the camera
         light never outlives the dialog.

         It reads on its own: there is no shutter. The first code found closes
         the dialog and is emitted as `detected`; what it means is the caller's
         business. -->
    <v-dialog v-model="open" :fullscreen="isPhone" max-width="600">
        <v-card>
            <v-toolbar density="comfortable" color="transparent">
                <v-card-title>{{ title || t("scan_qr") }}</v-card-title>
                <v-spacer />
                <v-btn v-if="cameras.length > 1" icon variant="text" @click="switchCamera">
                    <v-icon>mdi-camera-flip-outline</v-icon>
                    <v-tooltip activator="parent" location="bottom">{{ t("switch_camera") }}</v-tooltip>
                </v-btn>
                <v-btn icon="mdi-close" variant="text" @click="close" />
            </v-toolbar>

            <v-card-text v-if="$slots.default" class="pt-0">
                <slot />
            </v-card-text>

            <v-card-text class="pa-0">
                <div class="stage">
                    <!-- `playsinline` or iOS Safari takes the video fullscreen
                         on its own; `muted` or autoplay is refused. -->
                    <video ref="video" class="stream" autoplay playsinline muted />
                    <div v-if="ready && !error" class="frame" />
                    <div v-if="error" class="overlay text-center pa-6">
                        <v-icon size="42" class="mb-3">mdi-camera-off-outline</v-icon>
                        <div>{{ error }}</div>
                    </div>
                    <v-progress-circular v-else-if="starting" class="overlay" indeterminate color="white" size="48" />
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.stage {
    position: relative;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 320px;
}

.stream {
    width: 100%;
    max-height: 70vh;
    display: block;
    object-fit: contain;
}

/* Where to hold the sticker. Decoding reads the whole frame, so this is a
   hint and not a crop. */
.frame {
    position: absolute;
    width: min(60%, 260px);
    aspect-ratio: 1;
    border: 3px solid rgba(255, 255, 255, 0.85);
    border-radius: 12px;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.25);
    pointer-events: none;
}

.overlay {
    position: absolute;
    color: #fff;
}
</style>

<script>
import { usePitStoreBasics } from "@/stores/app_common";
import { useIsPhone } from "@/composables/display";
import {
    hasUserMedia, openCameraStream, stopCameraStream, listCameras, streamDeviceId,
    SCAN_SIZE,
} from "@/utils/camera";
import { readQr } from "@/utils/qr";

// Time between two reads. Each read is awaited before the next is scheduled,
// so a slow decoder never piles frames up.
const SCAN_INTERVAL_MS = 150

export default {
    name: "DialogQrScanner",
    setup() {
        return { ...usePitStoreBasics(), ...useIsPhone() }
    },
    props: {
        modelValue: { type: Boolean, default: false },
        title: { type: String, default: "" },
    },
    emits: ["update:modelValue", "detected"],
    data: () => ({
        stream: null,
        cameras: [],
        // By id and not by position in `cameras` -- reopening the scanner on
        // index 0 lands on whichever camera the browser lists first, which is
        // usually the front one. See streamDeviceId().
        device_id: null,
        starting: false,
        ready: false,
        error: "",
        timer: null,
        // Bumped on every start and stop, so a read that was in flight when
        // the dialog closed cannot emit afterwards.
        session: 0,
    }),
    computed: {
        open: {
            get() { return this.modelValue },
            set(v) { this.$emit("update:modelValue", v) },
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(open) { open ? this.start() : this.stop() },
        },
    },
    beforeUnmount() {
        this.stop()
    },
    methods: {
        async start() {
            this.stop()
            const session = this.session
            this.error = ""
            if (!hasUserMedia()) {
                this.error = this.t("camera_unavailable")
                return
            }
            this.starting = true
            try {
                const stream = await openCameraStream(this.device_id, SCAN_SIZE)
                if (session !== this.session) {
                    // Closed while the permission prompt was up.
                    stopCameraStream(stream)
                    return
                }
                this.stream = stream
                // Hold on to the camera we actually got, so reopening asks
                // for that one again.
                this.device_id = streamDeviceId(stream) || this.device_id
                // The labels are blank until permission is granted, so the
                // list is only worth reading once the stream is open.
                this.cameras = await listCameras()
                await this.$nextTick()
                const video = this.$refs.video
                if (!video || session !== this.session) return
                video.srcObject = stream
                await video.play().catch(() => { })
                this.ready = true
                this.scheduleRead(session)
            } catch (e) {
                this.error = this.t("camera_unavailable")
                console.warn("Could not open the camera", e)
            } finally {
                this.starting = false
            }
        },
        stop() {
            this.session++
            clearTimeout(this.timer)
            this.timer = null
            stopCameraStream(this.stream)
            this.stream = null
            this.ready = false
            const video = this.$refs.video
            if (video) video.srcObject = null
        },
        scheduleRead(session) {
            this.timer = setTimeout(() => this.read(session), SCAN_INTERVAL_MS)
        },
        async read(session) {
            if (session !== this.session) return
            let text = ""
            try {
                text = await readQr(this.$refs.video)
            } catch (e) {
                // The decoder failing to load is not a frame without a code:
                // say so instead of spinning forever.
                if (session === this.session) {
                    this.error = this.t("camera_unavailable")
                    console.warn("Could not read QR codes", e)
                    stopCameraStream(this.stream)
                }
                return
            }
            if (session !== this.session) return
            if (text) {
                this.close()
                this.$emit("detected", text)
                return
            }
            this.scheduleRead(session)
        },
        async switchCamera() {
            const at = this.cameras.findIndex(c => c.deviceId === this.device_id)
            const next = this.cameras[(at + 1) % this.cameras.length]
            this.device_id = next?.deviceId || null
            await this.start()
        },
        close() {
            this.stop()
            this.open = false
        },
    },
}
</script>
